import React, { useContext, useState } from 'react'
import styled, { css, ThemeContext } from 'styled-components'
import { useTranslation } from 'react-i18next'

// Components
import Text from './Text'
import { Icon } from './icon'
import { RoundedCard } from './card/GenericCard'
import IconContainer, { Container } from './icon/IconContainer'
import JobTag from './tag/JobTag'
import { RowAligned } from './layout/LayoutAtoms'

// Styling
import { shadows, fastTransition } from '../styles/effects'

// Utils
import { useAppState, ITheme } from '../context/AppContext'
import { cultureValueColors } from '../utils/habitHelper'

// Types
import { IconType } from './icon/IconType'
import { IFeedbackCompetency } from '../common/models/Feedback'
import { TextTagType } from '../common/models/typography'

interface ICompetencyChipProps {
  competency: IFeedbackCompetency
  iconType?: IconType
  competencyFrom?: string
  focus?: boolean
  checked?: boolean
  selected?: boolean
  disabled?: boolean
  onClick?: () => void
  mainIcon?: IconType
  className?: string
  // Cypress handle, forwarded to the chip element.
  'data-cy'?: string
}

interface ICompetencyDetailsProps {
  name: string
  subtitle?: string
  // A raw CSS colour string (forwarded to the icon/container background), not a
  // theme-token key — mirrors the sibling subtitleColor/titleColor props.
  color?: string
  focus: boolean
  iconProps?: {
    size: number
    type: IconType
    color: string
  }
  subtitleColor?: string
  titleColor?: string
  disabled?: boolean
  withoutIcon?: boolean
  className?: string
  // Render the title as a heading tag so it picks up the brand heading font on
  // the public Employer Branding pages (where h1–h6 get the heading font).
  // Defaults to a <p>, leaving all in-app usages unchanged.
  titleTag?: TextTagType
}

export const CompetencyDetails: React.FC<ICompetencyDetailsProps> = ({
  color,
  name,
  subtitle,
  focus,
  iconProps,
  withoutIcon,
  subtitleColor,
  titleColor,
  disabled,
  className,
  titleTag,
}) => {
  return (
    <MiddleBlock className={className}>
      {withoutIcon ? (
        <StyledContainer backgroundColor={color} padding="8px" />
      ) : (
        iconProps && (
          <StyledIconContainer
            backgroundColor={color}
            padding={focus ? '14px' : '10px'}
            disabled={disabled}
            {...iconProps}
          />
        )
      )}
      <CompetencyText disabled={disabled}>
        {subtitle && (
          <StyledText
            tag="span"
            textColor={subtitleColor}
            textStyle={focus ? 'labelSmallCaps' : 'labelTinyCaps'}
          >
            {subtitle}
          </StyledText>
        )}
        <StyledText
          tag={titleTag}
          textColor={titleColor}
          textStyle={focus ? 'title' : 'labelHeavy'}
        >
          {name}
        </StyledText>
      </CompetencyText>
    </MiddleBlock>
  )
}

const CompetencyChip: React.FC<ICompetencyChipProps> = ({
  competency,
  competencyFrom,
  iconType,
  selected,
  checked,
  focus,
  className,
  disabled,
  onClick,
  mainIcon,
  'data-cy': dataCy,
}) => {
  const { t } = useTranslation()
  const { company } = useAppState()
  const theme = useContext(ThemeContext) as ITheme
  const [hovered, setHovered] = useState(false)

  const isSkill = competency.competency === 'skill'
  const iconProps = {
    size: focus ? 20 : 12,
    type: mainIcon || (isSkill ? 'Skills' : ('Culture' as IconType)),
    color: isSkill ? theme.colors.text72 : theme.colors.lightest,
  }

  const valColors =
    competency.color && company
      ? cultureValueColors(theme, competency.color, company)
      : undefined

  return (
    <StyledRoundedCard
      className={className}
      data-cy={dataCy}
      focus={focus}
      onClick={onClick}
      selected={selected}
      disabled={disabled ?? !onClick}
      isSkill={isSkill}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      backgroundColor={!isSkill ? valColors?.lightValueColor : undefined}
    >
      <CardContent focus={focus}>
        {focus && (
          <FocusHeader>
            <Icon size={16} type="IconPinFilled" color={theme.colors.text72} />
            <Text textStyle="labelSmallCaps" color="text72">
              {competencyFrom ? `${competencyFrom}'s` : t('general.company')}{' '}
              {t('general.focus')}
            </Text>
          </FocusHeader>
        )}
        <CompetencyDetails
          iconProps={iconProps}
          name={competency.name}
          subtitle={competency.subtitle}
          color={competency.color}
          focus={!!focus}
          subtitleColor={
            (competency.color && theme.colors[competency.color]) ||
            theme.colors.textLight
          }
          disabled={disabled}
        />
        {!focus && (
          <ActionIcons>
            {(competency.focusPoint || competency.priority) && (
              <Icon
                type="IconPinFilled"
                size={16}
                color={theme.colors.primary}
              />
            )}
            {!!competency.numberAssessments && (
              <StyledJobTag content={competency.numberAssessments} />
            )}

            {checked && (
              <Icon type="Check" size={12} color={theme.colors.primary} />
            )}

            {!(disabled || !onClick) && (
              <IconWrapper>
                <Icon
                  size={12}
                  type={
                    hovered && selected ? 'Close' : iconType ?? 'ChevronRight'
                  }
                  color={theme.colors.primaryFadeLight}
                />
              </IconWrapper>
            )}
          </ActionIcons>
        )}
      </CardContent>
      {focus && (
        <IconWrapper marginTop="16px">
          <Text textStyle="labelHeavy" textAlign="left" color="primaryFadeHalf">
            {t('feedback.giveFeedback')}
          </Text>
          <Icon
            size={12}
            type="ChevronRight"
            color={theme.colors.primaryFadeLight}
          />
        </IconWrapper>
      )}
    </StyledRoundedCard>
  )
}

const MiddleBlock = styled(RowAligned)<{ focus?: boolean }>`
  margin: auto 0;
  ${({ focus }) => focus && 'min-height: 68px;'}
`

const CardContent = styled.div<{ focus?: boolean }>`
  min-height: ${({ focus }) => (focus ? '100px' : 'auto')};
  display: flex;
  flex-direction: ${({ focus }) => (focus ? 'column' : 'row')};
  justify-content: space-between;
`

const FocusHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 16px;
`

export const CompetencyText = styled.div<{ disabled?: boolean }>`
  display: flex;
  flex-direction: column;
  opacity: ${({ disabled }) => (disabled ? '0.6' : '1')};
`

const StyledText = styled(Text)<{ textColor?: string }>`
  ${({ textColor }) =>
    textColor &&
    `
    color: ${textColor};
  `};
`

const StyledContainer = styled(Container)`
  margin-right: 12px;
`

const ActionIcons = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-left: 12px;
`

export const IconWrapper = styled.div<{ marginTop?: string }>`
  display: flex;
  align-items: center;
  gap: 5px;
  margin-top: ${(props) => props.marginTop || '0'};
`

const StyledJobTag = styled(JobTag)`
  border-radius: 8px;
`

const hoverSelectedStyles = (isSkill?: boolean) => css<{ theme: any }>`
  background-color: ${({ theme }) => isSkill && theme.colors.primaryFadeLight};

  cursor: pointer;

  ${IconWrapper} {
    * {
      color: ${({ theme }) => theme.colors.primary};
      fill: ${({ theme }) => theme.colors.primary};
    }
  }
`

export const StyledIconContainer = styled(IconContainer)`
  transition: background-color ${fastTransition};
  border-radius: 8px;
`

const StyledRoundedCard = styled(RoundedCard)<{
  focus?: boolean
  selected?: boolean
  disabled?: boolean
  isSkill?: boolean
  backgroundColor?: string
}>`
  border-radius: 8px;
  box-shadow: ${shadows.depthOne};
  padding: ${({ focus }) => (focus ? '16px' : '12px')};
  margin: ${({ focus }) => (focus ? '6px 0' : '12px 0')};

  ${({ backgroundColor }) =>
    backgroundColor &&
    `
    background-color: ${backgroundColor};

    ${StyledIconContainer} {
      border-radius: 50%;
    }
  `}

  &:hover {
    ${({ disabled, isSkill }) => !disabled && hoverSelectedStyles(isSkill)}

    ${StyledIconContainer} {
      background-color: ${({ theme, isSkill, disabled }) =>
        !disabled && isSkill && theme.colors.lightest};
    }
  }

  ${({ selected, disabled, isSkill, theme }) =>
    selected &&
    !disabled &&
    css`
      ${!disabled && hoverSelectedStyles(isSkill)}

      ${StyledIconContainer} {
        background-color: ${!disabled && isSkill && theme.colors.lightest};
      }
    `}
`
export default CompetencyChip
